import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Shield, ShieldCheck, ShieldAlert, ShieldX, RefreshCw, Ban, LockOpen,
  Activity, Users as UsersIcon, Server, Globe, ChevronRight, Clock, Save,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const STATUS_ICON = {
  ok: { icon: ShieldCheck, color: "#009A49" },
  info: { icon: Shield, color: "#3B82F6" },
  warn: { icon: ShieldAlert, color: "#FFA62B" },
  fail: { icon: ShieldX, color: "#F74B64" },
};

const GRADE_COLOR = {
  A: { bg: "#DCFCE7", fg: "#166534" },
  B: { bg: "#FEF3C7", fg: "#92400E" },
  C: { bg: "#FED7AA", fg: "#9A3412" },
  D: { bg: "#FECACA", fg: "#991B1B" },
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-JM", { timeZone: "America/Jamaica" }); }
  catch { return iso.slice(0, 19).replace("T", " "); }
};

export default function AdminSecurity() {
  const { user } = useAuth();
  const authorized = user && ["admin", "super_admin"].includes(user.role);

  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);
  const [failed, setFailed] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [posture, setPosture] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("overview");
  const [refreshing, setRefreshing] = useState(false);

  const reload = async () => {
    setRefreshing(true);
    try {
      const [ov, ev, fl, bl, tr, po, se] = await Promise.all([
        api.get("/admin/security/overview"),
        api.get("/admin/security/events?limit=150"),
        api.get("/admin/security/failed-logins"),
        api.get("/admin/security/blocked-ips"),
        api.get("/admin/security/traffic"),
        api.get("/admin/security/posture"),
        api.get("/admin/security/settings"),
      ]);
      setOverview(ov.data);
      setEvents(ev.data);
      setFailed(fl.data);
      setBlocked(bl.data);
      setTraffic(tr.data);
      setPosture(po.data);
      setSettings(se.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load security data");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!authorized) return;
    reload();
    // Auto-refresh every 30s
    const t = setInterval(() => { if (!document.hidden) reload(); }, 30000);
    return () => clearInterval(t);
  }, [authorized]);

  if (!authorized) {
    return (
      <div className="rounded-2xl bg-red-50 border border-red-200 p-6 text-sm text-red-800" data-testid="security-forbidden">
        <ShieldX className="inline mr-2" size={16} />
        Only Admins and Super Admins can view the Security Center.
      </div>
    );
  }

  const blockManual = async () => {
    const ip = prompt("IP address to block:");
    if (!ip) return;
    const reason = prompt("Reason (shown on the block list):", "Blocked by administrator") || "Blocked by administrator";
    const minsStr = prompt("Block duration in minutes (0 = permanent):", "0");
    const mins = Math.max(0, parseInt(minsStr || "0", 10) || 0);
    try {
      await api.post("/admin/security/block-ip", { ip: ip.trim(), reason, duration_minutes: mins });
      toast.success(`Blocked ${ip}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to block");
    }
  };

  const unblock = async (ip) => {
    try {
      await api.post("/admin/security/unblock-ip", { ip });
      toast.success(`Unblocked ${ip}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to unblock");
    }
  };

  const blockFromFailedList = async (ip) => {
    try {
      await api.post("/admin/security/block-ip", {
        ip, reason: "Blocked from failed-login list", duration_minutes: 60 * 24,
      });
      toast.success(`Blocked ${ip} for 24h`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to block");
    }
  };

  const saveSettings = async (patch) => {
    try {
      const next = { ...settings, ...patch };
      const r = await api.put("/admin/security/settings", next);
      setSettings(r.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    }
  };

  const grade = overview?.posture_grade || "—";
  const gradeStyle = GRADE_COLOR[grade] || { bg: "#F4F4F5", fg: "#3F3F46" };

  return (
    <div data-testid="admin-security">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
        <div>
          <div className="overline text-zinc-500 inline-flex items-center gap-1.5">
            <Shield size={11} /> Security Center
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            Wordfence-style security overview
          </h1>
        </div>
        <button onClick={reload} disabled={refreshing}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50"
                data-testid="security-refresh">
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Auto-refreshes every 30 seconds. Visible only to Admin & Super Admin roles.
      </p>

      {/* Top cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Requests / 24h" value={overview?.events_24h ?? "—"} icon={Activity} tint="#3B82F6" testid="stat-events-24h" />
        <Stat label="Failed logins / 24h" value={overview?.failed_logins_24h ?? "—"} icon={UsersIcon} tint="#FFA62B" testid="stat-failed-logins" />
        <Stat label="Blocked IPs" value={overview?.blocked_ip_count ?? "—"} icon={Ban} tint="#F74B64" testid="stat-blocked" />
        <div className="rounded-2xl bg-white border border-zinc-200 p-4" data-testid="stat-posture">
          <div className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold mb-1 inline-flex items-center gap-1.5">
            <Shield size={11} /> Posture
          </div>
          <div className="flex items-baseline gap-2">
            <div className="font-display text-3xl font-extrabold">{overview?.posture_score ?? "—"}</div>
            <span className="px-2 py-0.5 rounded-full text-xs font-bold"
                  style={{ background: gradeStyle.bg, color: gradeStyle.fg }}>
              {grade}
            </span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            {overview ? `${overview.checks_total - overview.checks_failing}/${overview.checks_total} checks passing` : ""}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-zinc-200 mb-5">
        {[
          ["overview", "Overview"],
          ["events", "Live activity"],
          ["failed", "Failed logins"],
          ["blocked", "Blocked IPs"],
          ["traffic", "Traffic"],
          ["settings", "Settings"],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
                  className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${
                    tab === id ? "border-[#F74B64] text-[#F74B64]" : "border-transparent text-zinc-500 hover:text-zinc-900"
                  }`}
                  data-testid={`security-tab-${id}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && posture && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="posture-checks">
          {posture.checks.map((c) => {
            const meta = STATUS_ICON[c.status] || STATUS_ICON.info;
            const Icon = meta.icon;
            return (
              <div key={c.id} className="rounded-xl border border-zinc-200 bg-white p-4 flex items-start gap-3"
                   data-testid={`posture-check-${c.id}`}>
                <div className="w-9 h-9 rounded-full inline-flex items-center justify-center flex-shrink-0"
                     style={{ background: `${meta.color}15`, color: meta.color }}>
                  <Icon size={16} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{c.label}</div>
                  <div className="text-xs text-zinc-500 leading-relaxed mt-0.5">{c.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "events" && (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-x-auto" data-testid="events-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Time</th>
                <th className="text-left px-4 py-2 font-semibold">Event</th>
                <th className="text-left px-4 py-2 font-semibold">IP</th>
                <th className="text-left px-4 py-2 font-semibold">Path / detail</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr><td colSpan={4} className="text-center py-8 text-zinc-400">No security events yet.</td></tr>
              )}
              {events.map((e, i) => (
                <tr key={i} className="border-t border-zinc-100 align-top">
                  <td className="px-4 py-2 whitespace-nowrap text-zinc-500 text-xs">{fmtTime(e.created_at)}</td>
                  <td className="px-4 py-2"><KindPill kind={e.kind} /></td>
                  <td className="px-4 py-2 font-mono text-xs">{e.ip || "—"}</td>
                  <td className="px-4 py-2 text-zinc-600 text-xs">
                    {e.path || (e.email ? `email: ${e.email}` : "")}
                    {e.detail && Object.keys(e.detail).length > 0 && (
                      <span className="text-zinc-400"> · {JSON.stringify(e.detail)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "failed" && (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-x-auto" data-testid="failed-logins-table">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">IP</th>
                <th className="text-left px-4 py-2 font-semibold">Attempts (24h)</th>
                <th className="text-left px-4 py-2 font-semibold">Last seen</th>
                <th className="text-left px-4 py-2 font-semibold">Emails tried</th>
                <th className="text-right px-4 py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {failed.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-zinc-400">No failed logins in the last 24h. 🎉</td></tr>
              )}
              {failed.map((row) => (
                <tr key={row.ip} className="border-t border-zinc-100">
                  <td className="px-4 py-2 font-mono text-xs">{row.ip}</td>
                  <td className="px-4 py-2 font-semibold">{row.count}</td>
                  <td className="px-4 py-2 text-xs text-zinc-500">{fmtTime(row.last_seen)}</td>
                  <td className="px-4 py-2 text-xs text-zinc-600 truncate max-w-xs">
                    {row.emails.slice(0, 3).join(", ")}
                    {row.emails.length > 3 && ` + ${row.emails.length - 3}`}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {row.blocked ? (
                      <button onClick={() => unblock(row.ip)}
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                              data-testid={`unblock-${row.ip}`}>
                        <LockOpen size={11} /> Unblock
                      </button>
                    ) : (
                      <button onClick={() => blockFromFailedList(row.ip)}
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#F74B64] text-white hover:bg-[#e23a52]"
                              data-testid={`block-${row.ip}`}>
                        <Ban size={11} /> Block 24h
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "blocked" && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-zinc-500">{blocked.length} IP{blocked.length === 1 ? "" : "s"} currently blocked</div>
            <button onClick={blockManual}
                    className="btn-primary text-xs inline-flex items-center gap-1.5"
                    data-testid="block-manual">
              <Ban size={12} /> Block an IP
            </button>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white overflow-x-auto" data-testid="blocked-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">IP</th>
                  <th className="text-left px-4 py-2 font-semibold">Reason</th>
                  <th className="text-left px-4 py-2 font-semibold">Blocked by</th>
                  <th className="text-left px-4 py-2 font-semibold">Since</th>
                  <th className="text-left px-4 py-2 font-semibold">Expires</th>
                  <th className="text-right px-4 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {blocked.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-zinc-400">No IPs blocked.</td></tr>
                )}
                {blocked.map((b) => (
                  <tr key={b.ip} className="border-t border-zinc-100">
                    <td className="px-4 py-2 font-mono text-xs">{b.ip}</td>
                    <td className="px-4 py-2 text-xs">{b.reason}</td>
                    <td className="px-4 py-2 text-xs text-zinc-500">{b.blocked_by}</td>
                    <td className="px-4 py-2 text-xs text-zinc-500">{fmtTime(b.created_at)}</td>
                    <td className="px-4 py-2 text-xs text-zinc-500">
                      {b.expires_at
                        ? new Date(b.expires_at * 1000).toLocaleString("en-JM", { timeZone: "America/Jamaica" })
                        : "Permanent"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => unblock(b.ip)}
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                              data-testid={`unblock-btn-${b.ip}`}>
                        <LockOpen size={11} /> Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "traffic" && traffic && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="traffic-panel">
          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="overline text-zinc-500 mb-2 inline-flex items-center gap-1.5">
              <Globe size={11} /> Top IPs (last 24h)
            </div>
            <ul className="text-sm divide-y divide-zinc-100">
              {traffic.top_ips.length === 0 && <li className="py-4 text-zinc-400 text-center">No traffic yet.</li>}
              {traffic.top_ips.map((r) => (
                <li key={r.ip} className="flex justify-between py-2">
                  <span className="font-mono text-xs">{r.ip}</span>
                  <span className="text-zinc-500 text-xs">{r.count} req</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="overline text-zinc-500 mb-2 inline-flex items-center gap-1.5">
              <Server size={11} /> Top endpoints (last 24h)
            </div>
            <ul className="text-sm divide-y divide-zinc-100">
              {traffic.top_paths.length === 0 && <li className="py-4 text-zinc-400 text-center">No traffic yet.</li>}
              {traffic.top_paths.map((r) => (
                <li key={r.path} className="flex justify-between py-2 gap-3">
                  <span className="font-mono text-[11px] truncate">{r.path}</span>
                  <span className="text-zinc-500 text-xs whitespace-nowrap">{r.count} req</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-4 md:col-span-2">
            <div className="overline text-zinc-500 mb-2 inline-flex items-center gap-1.5">
              <Clock size={11} /> Hourly volume
            </div>
            <Sparkbars data={traffic.hourly} />
          </div>
        </div>
      )}

      {tab === "settings" && settings && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 max-w-xl" data-testid="settings-panel">
          <div className="overline text-zinc-500 mb-3">Auto-block rule</div>
          <label className="flex items-center gap-2 text-sm mb-4">
            <input
              type="checkbox"
              checked={!!settings.auto_block_enabled}
              onChange={(e) => saveSettings({ auto_block_enabled: e.target.checked })}
              data-testid="auto-block-toggle"
            />
            Enable auto-block on repeated failed logins
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-zinc-600">Threshold (fails / 15min)</span>
              <input type="number" min="3" max="50"
                     value={settings.auto_block_threshold}
                     onChange={(e) => setSettings({ ...settings, auto_block_threshold: Number(e.target.value) })}
                     className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
                     data-testid="auto-block-threshold" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-zinc-600">Cool-down (minutes)</span>
              <input type="number" min="5" max="1440"
                     value={settings.auto_block_minutes}
                     onChange={(e) => setSettings({ ...settings, auto_block_minutes: Number(e.target.value) })}
                     className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
                     data-testid="auto-block-minutes" />
            </label>
          </div>
          <button onClick={() => saveSettings({
                    auto_block_threshold: settings.auto_block_threshold,
                    auto_block_minutes: settings.auto_block_minutes,
                  })}
                  className="btn-primary mt-4 inline-flex items-center gap-2 text-sm"
                  data-testid="save-security-settings">
            <Save size={14} /> Save
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tint, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-zinc-200 p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold mb-1 inline-flex items-center gap-1.5">
        <Icon size={11} style={{ color: tint }} /> {label}
      </div>
      <div className="font-display text-3xl font-extrabold">{value}</div>
    </div>
  );
}

function KindPill({ kind }) {
  const styles = {
    "login.success": { bg: "#DCFCE7", fg: "#166534" },
    "login.failed": { bg: "#FEE2E2", fg: "#991B1B" },
    "ip.blocked": { bg: "#FED7AA", fg: "#9A3412" },
    "ip.auto_blocked": { bg: "#FECACA", fg: "#7F1D1D" },
    "ip.unblocked": { bg: "#DBEAFE", fg: "#1E40AF" },
    request: { bg: "#F4F4F5", fg: "#3F3F46" },
  };
  const s = styles[kind] || { bg: "#F4F4F5", fg: "#3F3F46" };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide"
          style={{ background: s.bg, color: s.fg }}>
      <ChevronRight size={9} /> {kind}
    </span>
  );
}

function Sparkbars({ data }) {
  if (!data || data.length === 0) return <div className="text-xs text-zinc-400">No data.</div>;
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-24 mt-1">
      {data.map((d) => (
        <div key={d.hour} className="flex-1 min-w-[6px] group relative">
          <div className="w-full rounded-t bg-gradient-to-t from-[#F74B64] to-[#FFA62B]"
               style={{ height: `${(d.count / max) * 100}%` }} />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap bg-zinc-900 text-white text-[10px] px-1.5 py-0.5 rounded">
            {d.hour} · {d.count}
          </div>
        </div>
      ))}
    </div>
  );
}
